// prefs-options.ts — single source of truth for the option keys used by
// the signup PreferencesScreen and the in-workspace edit modal.
//
// Why centralize?
//   - The workspace edit modal (A4) reuses PreferencesScreen's option
//     sets. Two diverging copies would drift (e.g. add "core" to areaOpts
//     in Auth.tsx but forget to update the workspace edit modal → user
//     can pick it at signup but can't edit it later, and vice-versa).
//   - i18n keys are generated as `auth.prefs.{group}Opts.{key}` and the
//     keys MUST match the i18n JSON. Keeping them in one TS const lets
//     us run lint/test invariants over the pair.

export const BUDGET_BUCKETS: Record<string, { min: number; max: number | null }> = {
  under800k:    { min: 0,         max: 800_000 },
  "800k-1.2m":  { min: 800_000,   max: 1_200_000 },
  "1.2m-1.8m":  { min: 1_200_000, max: 1_800_000 },
  "1.8m-3m":    { min: 1_800_000, max: 3_000_000 },
  over3m:       { min: 3_000_000, max: null },
};

export const BUDGET_KEYS = Object.keys(BUDGET_BUCKETS) as Array<keyof typeof BUDGET_BUCKETS>;
export const AREA_OPTS = ["central", "east", "west", "north", "northeast", "any"] as const;
export const TIMELINE_OPTS = ["0-3mo", "3-6mo", "6-12mo", "exploring"] as const;
export const PRIORITY_OPTS = ["mrt", "schools", "size", "newLaunch", "quiet", "yield"] as const;

export type AreaOpt = (typeof AREA_OPTS)[number];
export type TimelineOpt = (typeof TIMELINE_OPTS)[number];
export type PriorityOpt = (typeof PRIORITY_OPTS)[number];

/** Reverse-lookup: numeric (min,max) → bucket key. Used when seeding the
 *  edit modal with values that came back from the backend. */
export function bucketFromRange(min: number | null | undefined, max: number | null | undefined): string {
  if (min == null && max == null) return "";
  for (const k of BUDGET_KEYS) {
    const b = BUDGET_BUCKETS[k];
    if ((b.min ?? 0) === (min ?? 0) && (b.max ?? null) === (max ?? null)) return k;
  }
  return "";
}
